/**
 * ATT С2: вложения на экранах переписки 1:1 — ОБЩИЙ кусок страницы владельца
 * (pages/Msg/msgChat) и участника (pages/Invite/memberChat). Кусок один
 * намеренно: у вложения нет «стороны», и две копии рендера разошлись бы
 * первой же правкой — как разошлись бы правила доверия, живи они в двух
 * местах (тот же довод, что у msgRuntime).
 *
 * Правила показа — из решения владельца 03.09 (attachments.js, canRenderInline):
 * картинка белого списка — <img> из blob:-URL прямо в ленте, ВСЁ остальное —
 * строка со «Скачать». SVG инлайном никогда.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  canRenderInline, cipherSizeOf, humanSize, MAX_ATTACHMENT_BYTES, playableVideoMime,
} from './attachments';

/** Отказ скачивания — на человеческий. Коды бросает decryptAttachment и
 *  MsgApiError; странице важно различие «файл битый» / «файла уже нет». */
export function attachmentErrorText(e) {
  if (e?.code === 'sha_mismatch') return 'Файл повреждён или подменён — показывать его нельзя.';
  if (e?.code === 'undecryptable') return 'Не удалось расшифровать файл.';
  if (e?.code === 'bad_meta') return 'Вложение повреждено.';
  // Файл ЦЕЛЫЙ (sha сошлась) и расшифрован, но картинкой не открывается:
  // тип в метаданных не совпал с содержимым или снимок битый у отправителя.
  if (e?.code === 'undecodable') return 'Файл не открывается как картинка — скачайте его.';
  // Видео: файл целый и расшифрован, но браузер его не проигрывает — чаще
  // всего кодек (HEVC с айфона в Chrome), а не порча.
  if (e?.code === 'unplayable') return 'Видео не воспроизводится в этом браузере — скачайте его.';
  if (e?.status === 404) return 'Файла уже нет на сервере — срок хранения вышел.';
  return 'Не удалось скачать файл — проверьте связь и попробуйте ещё раз.';
}

/** Отдать расшифрованный blob браузеру как сохранение файла. */
function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Имя — из ЧУЖОГО шифротекста: разделители путей в download-атрибуте
  // браузер и сам не пустит, но полагаться на это не обязаны.
  a.download = String(name || 'файл').replace(/[\\/]/g, '_');
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Отзыв сразу оборвал бы асинхронное сохранение; минута — с запасом.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Строка «📎 имя (размер) [Скачать]» — путь всех не-картинок.
 *
 * `focusOnMount` — строка появилась ВМЕСТО того, на чём стоял фокус
 * (картинка не открылась, см. InlineImage): фокус надо принять на себя,
 * иначе он падает на body и человек ищет сообщение с начала документа.
 */
function DownloadRow({
  meta, fetchBlob, failed = null, focusOnMount = false,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(failed);
  const btnRef = useRef(null);
  // id нужен для aria-describedby: без связи кнопка читается голым
  // «Скачать», и причина — почему картинки нет — читалке не достаётся.
  const errorId = `attach-error-${meta.id}`;
  useEffect(() => {
    if (focusOnMount) btnRef.current?.focus();
  }, [focusOnMount]);
  const click = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      saveBlob(await fetchBlob(meta), meta.name);
    } catch (e) {
      setError(attachmentErrorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="msgAttachRow" data-testid="attach-row">
      <span className="msgAttachName">📎 {meta.name || 'файл'}</span>
      <span className="msgAttachSize">{humanSize(meta.size)}</span>
      <button
        type="button"
        ref={btnRef}
        onClick={click}
        disabled={busy}
        aria-describedby={error ? errorId : undefined}
      >
        {busy ? 'Скачиваем…' : 'Скачать'}
      </button>
      {/* role="status" — отказ обязан ОБЪЯВЛЯТЬСЯ, а не только рисоваться:
          человек мог смотреть в другое место экрана или не смотреть вовсе. */}
      {error && (
        <span
          className="msgAttachError"
          id={errorId}
          role="status"
          data-testid="attach-error"
        >
          {error}
        </span>
      )}
    </span>
  );
}

/**
 * ATT C2a: полноэкранный просмотр картинки (заявка владельца 11.09).
 * blob:-URL здесь ЧУЖОЙ — им владеет InlineImage, лайтбокс только
 * показывает: второй fetchBlob означал бы вторую расшифровку того же
 * файла и второй расшифрованный экземпляр в памяти телефона.
 */
function Lightbox({ url, name, onClose, onImageError }) {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  /**
   * Просмотр рендерится ПОРТАЛОМ в body, а не там, где стоит картинка.
   *
   * 🔴 Что здесь ПРОВЕРЕНО, а что нет. Ревью 12.09 (р.3) предполагало, что
   * оверлей-потомок `.msgScroll` отдаёт ей колесо и клавиши, и переписка
   * уезжает под просмотром. Замер этого НЕ подтвердил: Chrome не прокручивает
   * контейнер, вместе с которым элемент не движется, — ни колесом, ни PageUp
   * (зонд: 812 -> 812 у fixed И absolute потомка, 812 -> 412 -> 152 без
   * оверлея). Портал оставлен не поэтому, а потому что так поведение не
   * зависит от эвристик браузера, `aria-modal` перестаёт быть обещанием,
   * которого разметка не держит, и оверлей не сожмётся, если у предка
   * когда-нибудь появится transform/filter/contain. Структуру сторожит пин
   * «просмотр живёт ПОРТАЛОМ в body» (attachmentsUi.test.jsx).
   */
  return createPortal((
    // Клик по фону = закрыть: правило любого просмотрщика. Клавиатуре то же
    // самое даёт Escape, поэтому у подложки роли кнопки нет.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className="msgLightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Просмотр «${name}»`}
      data-testid="attach-lightbox"
      onClick={onClose}
      // Фокусируемый элемент в диалоге один (крестик) — Tab не выпускаем к
      // странице под оверлеем: для читалки диалог «модальный», пусть таким и
      // будет для клавиатуры.
      onKeyDown={(e) => {
        if (e.key === 'Tab') { e.preventDefault(); closeRef.current?.focus(); }
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <img
        src={url}
        alt={name}
        onClick={(e) => e.stopPropagation()}
        // Картинка может не декодироваться и здесь (тот же URL): тогда
        // просмотр закрывается, а вложение уходит в «Скачать» — держать
        // открытым чёрный экран не за чем.
        onError={onImageError}
      />
      <button
        type="button"
        className="msgLightboxClose"
        ref={closeRef}
        aria-label="Закрыть просмотр"
        onClick={onClose}
      >
        ✕
      </button>
    </div>
  ), document.body);
}

/**
 * Картинка в ленте: скачивается и расшифровывается сама при появлении.
 * Отказ не прячется — строка со «Скачать» и честным текстом: картинка,
 * которой молча нет, неотличима от непосланной.
 */
function InlineImage({ meta, fetchBlob }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  // C2a: открыт ли полноэкранный просмотр. Живёт здесь же, где URL: лайтбокс
  // не переживает свою картинку (unmount ленты закрывает и его — и revoke
  // на размонтировании остаётся единственным владельцем URL).
  const [open, setOpen] = useState(false);
  // Отказ случился, когда фокус был ВНУТРИ картинки или её просмотра: тогда
  // строка «Скачать» обязана принять фокус на себя (см. onImageError).
  const [errorFocus, setErrorFocus] = useState(false);
  const openBtnRef = useRef(null);
  // URL, который уже отозван по ошибке декодирования: cleanup эффекта не
  // должен отзывать его во второй раз (revoke идемпотентен, но считать этот
  // blob живым до размонтирования нельзя — он мёртв с момента отказа).
  const revokedRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const blob = await fetchBlob(meta);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setError(e);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl && objectUrl === revokedRef.current) return;
      // 🔴 revoke на размонтировании обязателен: blob:-URL держит
      // РАСШИФРОВАННЫЙ файл в памяти вкладки, и без отзыва листание
      // переписки с картинками копит десятки МБ на телефоне (план §2.2).
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // Идентичность вложения — meta.id: объект метаданных пересоздаётся на
    // каждый снимок состояния контроллера, перегружать картинку из-за этого
    // нельзя.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.id]);
  if (error) {
    return (
      <DownloadRow
        meta={meta}
        fetchBlob={fetchBlob}
        failed={attachmentErrorText(error)}
        focusOnMount={errorFocus}
      />
    );
  }
  if (!url) {
    return (
      <span className="msgAttachLoading" data-testid="attach-loading">
        Загружаем «{meta.name}»…
      </span>
    );
  }
  const close = () => {
    setOpen(false);
    // Фокус возвращается туда, откуда просмотр открыли, — иначе после Escape
    // он падает на body и клавиатурный путь по ленте начинается сначала.
    openBtnRef.current?.focus();
  };
  /**
   * 🔴 Файл ЦЕЛЫЙ (sha сошлась) и расшифрован, но `<img>` его не открыл:
   * тип в метаданных не совпал с содержимым (mime приходит из ЧУЖОГО
   * шифротекста) или снимок битый ещё у отправителя. Без этого пути
   * оставалась пустая рамка с живой кнопкой, «Скачать» не появлялось вовсе —
   * то есть файл был недоступен человеку НИКАК, и почему, он не узнавал
   * (находка ревью 12.09, р.3, Medium).
   */
  const onImageError = () => {
    // 🔴 Фокус передаём «Скачать» ТОЛЬКО если он был здесь — на крестике
    // открытого просмотра или на самой картинке. Иначе (человек в это время
    // печатает сообщение) угон фокуса из-за чужой картинки хуже самой
    // ошибки. Находка ревью 12.09, р.4, Medium — про правку р.3.
    setErrorFocus(open || openBtnRef.current === document.activeElement);
    setOpen(false);
    URL.revokeObjectURL(url);
    revokedRef.current = url;
    setUrl(null);
    setError({ code: 'undecodable' });
  };
  return (
    <>
      {/* Кнопка-обёртка: клик/Enter/Space открывают просмотр нативно, без
          самодельных key-обработчиков на <img> */}
      <button
        type="button"
        className="msgAttachImgBtn"
        ref={openBtnRef}
        aria-label={`Открыть «${meta.name}» на весь экран`}
        onClick={() => setOpen(true)}
      >
        <img
          className="msgAttachImg"
          src={url}
          alt={meta.name}
          data-testid="attach-img"
          onError={onImageError}
        />
      </button>
      {open && (
        <Lightbox
          url={url}
          name={meta.name}
          onClose={close}
          onImageError={onImageError}
        />
      )}
    </>
  );
}

/**
 * Закрыть открытое видео — одно на страницу.
 *
 * 🔴 Расшифровка у нас целиком в память (один AES-GCM на файл, потока нет),
 * а видео до 25 МБ: три открытых ролика — это шифротекст, открытый текст и
 * буферы декодера трижды, на телефоне. Открывая следующее, предыдущее
 * сворачиваем и отпускаем.
 */
let closeOpenVideo = null;

/**
 * Видео в ленте (ВИДЕО В-1).
 *
 * 🔴 Скачивается и расшифровывается ТОЛЬКО по нажатию — не при появлении, как
 * картинка: десять роликов в переписке означали бы до 250 МБ расшифровки
 * разом. `preload` от этого не спасает — он действует уже после того, как файл
 * расшифрован.
 *
 * Отказы:
 * - сеть, 404, порча — текст ошибки у кнопки, можно нажать ещё раз;
 * - файл целый, но браузер его не играет (`error` у `<video>`, чаще кодек) —
 *   ресурс отпускается, остаётся «Скачать» с честной причиной;
 * - Safari не дал запустить звук после асинхронной загрузки
 *   (`NotAllowedError`) — это НЕ ошибка: плеер с кнопкой Play на месте.
 *
 * «Скачать» доступно всегда: плеер может зависнуть без события ошибки.
 */
function InlineVideo({ meta, mime, fetchBlob }) {
  const [phase, setPhase] = useState('idle'); // idle | loading | ready | failed
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [failFocus, setFailFocus] = useState(false);
  const mountedRef = useRef(true);
  const pendingRef = useRef(false);
  const opRef = useRef(0);
  const urlRef = useRef(null);
  // 🔴 Узел плеера — в СВОЁМ ref, а не в ref={useRef}: к cleanup эффекта при
  // размонтировании React уже обнуляет обычный ref, и pause/снятие src/load
  // молча не выполнялись — декодер не отпускался (ревью В-1, P2). Колбэк
  // игнорирует null, узел забывает только release.
  const mediaNodeRef = useRef(null);
  const setMediaNode = useRef((el) => { if (el) mediaNodeRef.current = el; }).current;
  const cardRef = useRef(null);
  const openBtnRef = useRef(null);
  const refocusOpenRef = useRef(false);
  const closeRef = useRef(null);

  // Отпустить ресурс: незавершённая загрузка теряет право что-то показать,
  // плеер останавливается и забывает источник, blob:-URL отзывается.
  const release = () => {
    opRef.current += 1;
    const v = mediaNodeRef.current;
    mediaNodeRef.current = null;
    if (v) {
      try {
        v.pause();
        v.removeAttribute('src');
        v.load();
      } catch {
        // jsdom не умеет медиа — в браузере эти вызовы не бросают
      }
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    if (closeOpenVideo === closeRef.current) closeOpenVideo = null;
  };
  if (!closeRef.current) {
    closeRef.current = () => {
      release();
      if (!mountedRef.current) return;
      setUrl(null);
      setPhase('idle');
    };
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      release();
    };
    // release читает только ref'ы — перезаводить эффект на каждый рендер незачем
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === 'ready') {
      // Попытка запуска. Отказ (NotAllowedError на iOS) глотаем: плеер уже
      // показан с управлением, человек нажмёт Play сам.
      try {
        const p = mediaNodeRef.current?.play?.();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch {
        // старые движки бросают синхронно — то же самое
      }
    }
    if (phase === 'idle' && refocusOpenRef.current) {
      refocusOpenRef.current = false;
      openBtnRef.current?.focus();
    }
  }, [phase]);

  const open = async () => {
    if (pendingRef.current || urlRef.current) return;
    if (closeOpenVideo && closeOpenVideo !== closeRef.current) closeOpenVideo();
    closeOpenVideo = closeRef.current;
    pendingRef.current = true;
    opRef.current += 1;
    const op = opRef.current;
    setError(null);
    setPhase('loading');
    try {
      let blob = await fetchBlob(meta);
      if (!mountedRef.current || op !== opRef.current) return;
      // Тип мог прийти пустым (octet-stream) — даём плееру тот, что вычислили
      // по имени; байты те же, копии нет.
      if (blob.type !== mime) blob = blob.slice(0, blob.size, mime);
      const objectUrl = URL.createObjectURL(blob);
      urlRef.current = objectUrl;
      setUrl(objectUrl);
      setPhase('ready');
    } catch (e) {
      if (!mountedRef.current || op !== opRef.current) return;
      if (closeOpenVideo === closeRef.current) closeOpenVideo = null;
      setError(attachmentErrorText(e));
      setPhase('idle');
    } finally {
      pendingRef.current = false;
    }
  };

  const collapse = () => {
    refocusOpenRef.current = true;
    closeRef.current();
  };

  const onVideoError = () => {
    // Событие от уже отпущенного плеера (release снимает src) — не отказ.
    if (!urlRef.current) return;
    // Фокус забираем, если он был где угодно внутри карточки — плеер, «Свернуть»,
    // «Скачать»: всю карточку заменяет строка «Скачать» (ревью В-1, P3). Из поля
    // ввода не забираем.
    setFailFocus(Boolean(cardRef.current?.contains(document.activeElement)));
    release();
    setUrl(null);
    setPhase('failed');
  };

  if (phase === 'failed') {
    return (
      <DownloadRow
        meta={meta}
        fetchBlob={fetchBlob}
        failed={attachmentErrorText({ code: 'unplayable' })}
        focusOnMount={failFocus}
      />
    );
  }
  return (
    <span className="msgAttachVideo" data-testid="attach-video" ref={cardRef}>
      {phase === 'ready' ? (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={setMediaNode}
            className="msgAttachVideoPlayer"
            data-testid="attach-video-player"
            src={url}
            controls
            // 🔴 Без playsinline iOS уводит ролик в полноэкранный
            // проигрыватель — на телефоне это выглядит как «чат исчез».
            playsInline
            preload="metadata"
            aria-label={meta.name}
            onError={onVideoError}
          />
          <button
            type="button"
            className="msgAttachVideoBtn"
            data-testid="attach-video-close"
            onClick={collapse}
          >
            Свернуть видео
          </button>
        </>
      ) : (
        <button
          type="button"
          className="msgAttachVideoBtn"
          ref={openBtnRef}
          data-testid="attach-video-open"
          disabled={phase === 'loading'}
          aria-label={`Смотреть видео «${meta.name}»`}
          onClick={open}
        >
          {phase === 'loading' ? 'Загружаем видео…' : '▶ Смотреть видео'}
        </button>
      )}
      {error && (
        <span className="msgAttachError" role="status" data-testid="attach-video-error">
          {error}
        </span>
      )}
      <DownloadRow meta={meta} fetchBlob={fetchBlob} />
    </span>
  );
}

/** Только для тестов: модульное «открытое видео» переживает render/unmount. */
export function _resetOpenVideoForTests() {
  closeOpenVideo = null;
}

/**
 * Вложения одного сообщения. fetchBlob(meta) -> Promise<Blob> — отдаёт
 * страница (у неё контроллер); сам список о транспорте не знает и потому
 * один на владельца и участника.
 */
export function MsgAttachments({ attachments, fetchBlob }) {
  if (!attachments?.length) return null;
  return (
    <span className="msgAttachList" data-testid="msg-attachments">
      {/* 🔴 Ключ — meta.id, а не индекс: состояние картинки (blob:-URL,
          открытый просмотр, отказ декодирования) принадлежит КОНКРЕТНОМУ
          файлу. По индексу оно перешло бы на следующее вложение в том же
          слоте, и целый файл показался бы битым (пин «отказ не залипает»). */}
      {attachments.map((meta) => {
        // Картинка — прежний путь; видео — если браузер берётся его играть;
        // всё остальное — «Скачать».
        const videoMime = canRenderInline(meta.mime) ? null : playableVideoMime(meta);
        let body = <DownloadRow meta={meta} fetchBlob={fetchBlob} />;
        if (canRenderInline(meta.mime)) body = <InlineImage meta={meta} fetchBlob={fetchBlob} />;
        else if (videoMime) body = <InlineVideo meta={meta} mime={videoMime} fetchBlob={fetchBlob} />;
        return <span key={meta.id} className="msgAttach">{body}</span>;
      })}
    </span>
  );
}

/** Выбранный, но ещё не отправленный файл — чип над полем ввода. */
export function AttachmentDraft({ file, onClear }) {
  if (!file) return null;
  return (
    <div className="msgAttachDraft" data-testid="attach-draft">
      <span className="msgAttachName">📎 {file.name}</span>
      <span className="msgAttachSize">{humanSize(file.size)}</span>
      {/* Кап проверит и контроллер, но узнать про 25 МБ честнее ДО клика.
          Сверка — по шифротексту, как у сервера (ревью р.1, Medium). */}
      {cipherSizeOf(file.size) > MAX_ATTACHMENT_BYTES && (
        <span className="msgAttachError" data-testid="attach-too-large">
          больше {MAX_ATTACHMENT_BYTES / (1024 * 1024)} МБ — не уйдёт
        </span>
      )}
      <button type="button" onClick={onClear} aria-label="Убрать файл">✕</button>
    </div>
  );
}

/** Идущая загрузка (state.uploading контроллера): между кликом и конвертом. */
export function UploadingNote({ uploading }) {
  if (!uploading) return null;
  return (
    <div className="msgAttachUploading" data-testid="attach-uploading" role="status">
      Отправляем «{uploading.name}» ({humanSize(uploading.size)})…
      {uploading.percent != null ? ` ${uploading.percent}%` : ''}
    </div>
  );
}
